import {
  FEEDBACK_LIMITS,
  FeedbackContextValue,
  FeedbackIdSource,
} from '~/shared/utils/feedback-input';

export interface FeedbackEnvironment {
  pathname: string;
  search: string;
  viewportWidth: number;
  viewportHeight: number;
  userAgent: string;
  appVersion: string | null;
}

const SENSITIVE_PARAM = /token|code|secret|password|key|auth/i;

export function redactSearch(search: string): string {
  const params = new URLSearchParams(search);
  let changed = false;
  params.forEach((_value, name) => {
    if (SENSITIVE_PARAM.test(name)) {
      params.set(name, 'redacted');
      changed = true;
    }
  });
  if (!changed) {
    return search;
  }
  const rebuilt = params.toString();
  return rebuilt ? `?${rebuilt}` : '';
}

function viewportSide(value: number): number {
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded) || rounded < 1) {
    return 1;
  }
  return Math.min(rounded, FEEDBACK_LIMITS.viewportMax);
}

export function buildFeedbackContext(
  target: { feedbackId: string; source: FeedbackIdSource },
  env: FeedbackEnvironment
): FeedbackContextValue {
  const pathname = env.pathname.startsWith('/')
    ? env.pathname
    : `/${env.pathname}`;
  return {
    path: `${pathname}${redactSearch(env.search)}`.slice(
      0,
      FEEDBACK_LIMITS.path
    ),
    feedbackId: target.feedbackId.slice(0, FEEDBACK_LIMITS.feedbackId),
    feedbackIdSource: target.source,
    viewport: {
      width: viewportSide(env.viewportWidth),
      height: viewportSide(env.viewportHeight),
    },
    userAgent: env.userAgent.slice(0, FEEDBACK_LIMITS.userAgent),
    appVersion: env.appVersion
      ? env.appVersion.slice(0, FEEDBACK_LIMITS.appVersion)
      : null,
  };
}

export function appVersionFromManifest(manifest: unknown): string | null {
  if (!manifest || typeof manifest !== 'object') {
    return null;
  }
  const { version, client_js: clientJs } = manifest as Record<string, unknown>;
  if (typeof version !== 'string' || !version) {
    return null;
  }
  const bundle =
    typeof clientJs === 'string' ? clientJs.split('/').pop() : undefined;
  return bundle ? `${version} (${bundle})` : version;
}

export function readFeedbackEnvironment(): FeedbackEnvironment {
  const w = window as unknown as { __ASSET_MANIFEST__?: unknown };
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    userAgent: window.navigator.userAgent,
    appVersion: appVersionFromManifest(w.__ASSET_MANIFEST__),
  };
}
